/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import * as ToastPrimitive from '@radix-ui/react-toast'
import { CheckCircle2, AlertTriangle, Info, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

export interface ToastOptions {
  title: string
  description?: string
  variant?: 'default' | 'success' | 'destructive'
  duration?: number
}

interface ConfirmOptions {
  title: string
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'default' | 'destructive'
}

interface ToastRecord extends ToastOptions {
  id: string
  open: boolean
}

interface ConfirmState extends ConfirmOptions {
  open: boolean
}

interface FeedbackContextValue {
  toasts: ToastRecord[]
  toast: (options: ToastOptions) => void
  dismissToast: (id: string) => void
  removeToast: (id: string) => void
  confirmState: ConfirmState | null
  confirm: (options: ConfirmOptions) => Promise<boolean>
  resolveConfirm: (value: boolean) => void
}

const FeedbackContext = createContext<FeedbackContextValue | undefined>(undefined)

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([])
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null)
  const confirmResolverRef = useRef<((value: boolean) => void) | null>(null)

  const dismissToast = useCallback((id: string) => {
    setToasts((current) =>
      current.map((toast) => (
        toast.id === id ? { ...toast, open: false } : toast
      ))
    )
  }, [])

  const removeToast = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const toast = useCallback((options: ToastOptions) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    setToasts((current) => [
      ...current,
      {
        id,
        open: true,
        duration: 4500,
        variant: 'default',
        ...options,
      },
    ])
  }, [])

  const confirm = useCallback((options: ConfirmOptions) => {
    if (confirmResolverRef.current) {
      confirmResolverRef.current(false)
    }

    setConfirmState({
      open: true,
      cancelLabel: 'Cancel',
      confirmLabel: 'Confirm',
      variant: 'default',
      ...options,
    })

    return new Promise<boolean>((resolve) => {
      confirmResolverRef.current = resolve
    })
  }, [])

  const resolveConfirm = useCallback((value: boolean) => {
    if (confirmResolverRef.current) {
      confirmResolverRef.current(value)
      confirmResolverRef.current = null
    }
    setConfirmState(null)
  }, [])

  useEffect(() => () => {
    if (confirmResolverRef.current) {
      confirmResolverRef.current(false)
    }
  }, [])

  const value = useMemo<FeedbackContextValue>(() => ({
    toasts,
    toast,
    dismissToast,
    removeToast,
    confirmState,
    confirm,
    resolveConfirm,
  }), [confirm, confirmState, dismissToast, removeToast, resolveConfirm, toast, toasts])

  return (
    <FeedbackContext.Provider value={value}>
      {children}
    </FeedbackContext.Provider>
  )
}

function useFeedback() {
  const context = useContext(FeedbackContext)
  if (!context) {
    throw new Error('Feedback hooks must be used within a FeedbackProvider')
  }
  return context
}

export function useToast() {
  const { toast } = useFeedback()
  return { toast }
}

export function useConfirm() {
  const { confirm } = useFeedback()
  return { confirm }
}

export function Toaster() {
  const { toasts, dismissToast, removeToast, confirmState, resolveConfirm } = useFeedback()

  return (
    <>
      <ToastPrimitive.Provider swipeDirection="right">
        {toasts.map((toast) => {
          const variant = toast.variant ?? 'default'
          const Icon = variant === 'success'
            ? CheckCircle2
            : variant === 'destructive'
            ? AlertTriangle
            : Info

          return (
            <ToastPrimitive.Root
              key={toast.id}
              open={toast.open}
              duration={toast.duration}
              onOpenChange={(open) => {
                if (!open) {
                  dismissToast(toast.id)
                }
              }}
              onAnimationEnd={() => {
                if (!toast.open) {
                  removeToast(toast.id)
                }
              }}
              className={cn(
                'group pointer-events-auto relative grid w-full max-w-sm grid-cols-[auto_1fr_auto] items-start gap-3 overflow-hidden rounded-2xl border border-border/80 bg-card/95 p-4 text-card-foreground shadow-[0_24px_60px_rgba(15,23,42,0.18)] backdrop-blur',
                variant === 'success' && 'border-success/40 bg-success/10',
                variant === 'destructive' && 'border-destructive/40 bg-destructive/10',
              )}
            >
              <div
                className={cn(
                  'mt-0.5 rounded-full p-2',
                  variant === 'success' && 'bg-success/20 text-success',
                  variant === 'destructive' && 'bg-destructive/15 text-destructive',
                  variant === 'default' && 'bg-info/10 text-info',
                )}
              >
                <Icon className="h-4 w-4" />
              </div>
              <div className="space-y-1">
                <ToastPrimitive.Title className="text-sm font-semibold">
                  {toast.title}
                </ToastPrimitive.Title>
                {toast.description ? (
                  <ToastPrimitive.Description className="text-sm text-muted-foreground">
                    {toast.description}
                  </ToastPrimitive.Description>
                ) : null}
              </div>
              <ToastPrimitive.Close asChild>
                <button
                  type="button"
                  className="rounded-full p-1 text-muted-foreground transition-colors hover:bg-black/5 hover:text-foreground"
                  aria-label="Dismiss notification"
                >
                  <X className="h-4 w-4" />
                </button>
              </ToastPrimitive.Close>
            </ToastPrimitive.Root>
          )
        })}
        <ToastPrimitive.Viewport className="fixed top-4 right-4 z-[100] flex w-full max-w-sm flex-col gap-3 outline-none" />
      </ToastPrimitive.Provider>

      <AlertDialog
        open={Boolean(confirmState?.open)}
        onOpenChange={(open) => {
          if (!open) {
            resolveConfirm(false)
          }
        }}
      >
        <AlertDialogContent className="border-border/70 bg-card/95 shadow-[0_30px_80px_rgba(15,23,42,0.24)] backdrop-blur-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmState?.title}</AlertDialogTitle>
            {confirmState?.description ? (
              <AlertDialogDescription>{confirmState.description}</AlertDialogDescription>
            ) : null}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{confirmState?.cancelLabel ?? 'Cancel'}</AlertDialogCancel>
            <Button
              variant={confirmState?.variant === 'destructive' ? 'destructive' : 'default'}
              onClick={() => resolveConfirm(true)}
            >
              {confirmState?.confirmLabel ?? 'Confirm'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
